/**
 * Toast state — transient notification strip in the bottom-right of the
 * world canvas. Drives short-lived feedback: "Kicked alice: spam",
 * "Cannot demote the last Owner", rate-limit-rejected warnings (D16),
 * protocol errors (connection 4400). Toasts auto-dismiss after
 * `lifetime_ms` unless the user closes them sooner.
 *
 * Module-local timers ensure the auto-dismiss ticks independently of
 * render cadence; the store exposes a pure `push(toast)` action that
 * returns the assigned id so callers can dismiss manually.
 */

import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastState {
  toasts: Toast[];
  nextId: number;
  push: (kind: ToastKind, message: string, lifetime_ms?: number) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

const DEFAULT_LIFETIME_MS = 5000;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  nextId: 1,

  push: (kind, message, lifetime_ms) => {
    let id = -1;
    set((s) => {
      id = s.nextId;
      return {
        toasts: [...s.toasts, { id, kind, message }],
        nextId: s.nextId + 1,
      };
    });
    const timeout = lifetime_ms ?? DEFAULT_LIFETIME_MS;
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, timeout);
    }
    return id;
  },

  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  clear: () => set(() => ({ toasts: [] })),
}));
